/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { apiClient } from './client.js';

export type StaffType = 'all' | 'on_roll' | 'consultant';

/** Resolved query params for the analytics report (dates as YYYY-MM-DD, IST). */
export interface AnalyticsParams {
  readonly from?: string;
  readonly to?: string;
  readonly staffType?: StaffType;
}

export interface AnalyticsReport {
  readonly fromIso: string;
  readonly toIso: string;
  readonly staffType: StaffType;
  readonly kpis: {
    readonly revenuePaise: number;
    readonly sessions: number;
    readonly completed: number;
    readonly avgSatisfactionPct: number | null;
  };
  readonly statusBreakdown: readonly { readonly status: string; readonly count: number }[];
  readonly revenueByType: {
    readonly onRollPaise: number;
    readonly consultantPaise: number;
    readonly unattributedPaise: number;
  };
  readonly byTherapist: readonly {
    readonly id: string;
    readonly name: string;
    readonly category: 'on_roll' | 'consultant';
    readonly sessions: number;
    readonly completed: number;
    readonly satisfactionPct: number | null;
    readonly revenuePaise: number;
  }[];
  readonly monthly: readonly {
    readonly monthIso: string;
    readonly revenuePaise: number;
    readonly sessions: number;
  }[];
}

const BASE = '/admin/analytics';

async function download(path: string, params: AnalyticsParams, fileName: string): Promise<void> {
  const { data } = await apiClient.get<Blob>(path, { params, responseType: 'blob' });
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const analyticsApi = {
  async report(params: AnalyticsParams): Promise<AnalyticsReport> {
    const { data } = await apiClient.get<AnalyticsReport>(BASE, { params });
    return data;
  },
  async downloadPdf(params: AnalyticsParams): Promise<void> {
    await download(`${BASE}/export.pdf`, params, 'analytics.pdf');
  },
  async downloadXlsx(params: AnalyticsParams): Promise<void> {
    await download(`${BASE}/export.xlsx`, params, 'analytics.xlsx');
  },
};
